<template>
  <ib-dialog v-model="dialog" persistent :fullscreen="isMobile" :max-width="showContactPanel ? 1040 : 760">
    <v-card class="sandbox-card d-flex flex-column">
      <div class="sandbox-header d-flex align-center px-4 py-3">
        <v-avatar size="40" color="green100" class="mr-3 flex-shrink-0">
          <v-icon color="green700">{{ currentModeIcon }}</v-icon>
        </v-avatar>

        <div class="sandbox-header-info">
          <div class="sandbox-header-title text-truncate">{{ title || 'Teste de chatbot' }}</div>
          <div class="d-flex align-center flex-wrap">
            <ib-chip x-small color="#0B9055" class="mr-2">
              <v-icon x-small left>mdi-flask-outline</v-icon>
              Ambiente de teste
            </ib-chip>
            <span class="sandbox-header-subtitle">{{ currentModeLabel }}</span>
          </div>
        </div>

        <v-spacer />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon class="ml-1" :color="showContactPanel ? 'primary' : undefined" aria-label="Contato de teste" v-bind="attrs" v-on="on" @click="showContactPanel = !showContactPanel">
              <v-icon>mdi-account-cog-outline</v-icon>
            </v-btn>
          </template>
          <span>Contato de teste</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon class="ml-1" :disabled="!messages.length && !session" aria-label="Reiniciar teste" v-bind="attrs" v-on="on" @click="confirmRestart">
              <v-icon>mdi-restart</v-icon>
            </v-btn>
          </template>
          <span>Reiniciar teste</span>
        </v-tooltip>

        <v-btn icon class="ml-1" aria-label="Fechar" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <div class="sandbox-body d-flex flex-grow-1">
        <div v-show="!(isMobile && showContactPanel)" class="sandbox-chat-column flex-grow-1">
          <div ref="chat" class="sandbox-chat ib-scroll flex-grow-1" role="log" aria-live="polite">
            <div v-if="!messages.length" class="sandbox-empty d-flex flex-column align-center justify-center text-center">
              <v-avatar size="64" color="green100" class="mb-4">
                <v-icon size="32" color="green700">mdi-message-text-outline</v-icon>
              </v-avatar>
              <div class="sandbox-empty-title mb-1">Comece a conversa</div>
              <div class="sandbox-empty-text mb-4">
                Você é o contato. Envie uma mensagem e veja como o {{ type === 'flow_chatbot' ? 'fluxo' : 'chatbot' }} responde.
                Nada é enviado ao WhatsApp nem gravado na plataforma.
              </div>
              <div class="d-flex flex-wrap justify-center">
                <ib-chip v-for="suggestion in suggestions" :key="suggestion" color="#0B9055" class="ma-1 cursor-pointer" @click="send(suggestion)">
                  {{ suggestion }}
                </ib-chip>
              </div>
            </div>

            <template v-for="(item, index) in messages">
              <div v-if="item.type === 'event'" :key="`event_${index}`" class="sandbox-row sandbox-row-event">
                <div class="sandbox-event">
                  <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                  <span>{{ item.text }}</span>
                  <ib-chip v-for="tag in item.tags || []" :key="tag" x-small :color="tagColor(tag)" class="ml-1">
                    {{ tagName(tag) }}
                  </ib-chip>
                </div>
              </div>

              <div v-else :key="`message_${index}`" :class="`sandbox-row sandbox-row-${item.origin}`">
                <div class="sandbox-message-group">
                  <div :class="`sandbox-message sandbox-message-${item.origin}`">
                    <template v-if="item.type === 'media'">
                      <v-img v-if="item.media_type === 'image'" :src="item.url" contain max-height="260" class="sandbox-media mb-1" />

                      <video v-if="item.media_type === 'video'" controls class="sandbox-media mb-1">
                        <source :src="item.url" type="video/mp4">
                      </video>

                      <audio v-if="item.media_type === 'audio'" controls class="sandbox-audio mb-1">
                        <source :src="item.url" type="audio/mpeg">
                      </audio>

                      <a v-if="item.media_type === 'document'" :href="item.url" target="_blank" rel="noopener" class="sandbox-document d-flex align-center mb-1">
                        <v-icon class="mr-2">mdi-file-document-outline</v-icon>
                        <span class="text-truncate">{{ item.file_name || 'Documento' }}</span>
                      </a>
                    </template>

                    <div v-if="item.text || item.caption" class="sandbox-message-text" v-html="formatText(item.text || item.caption)"></div>

                    <div class="sandbox-message-time">{{ formatTime(item.created) }}</div>
                  </div>

                  <div v-if="item.type === 'buttons' && item.buttons?.length" class="sandbox-buttons">
                    <button v-for="(button, button_index) in item.buttons" :key="`button_${index}_${button_index}`" type="button" class="sandbox-button" :disabled="sending || ended" @click="send(button)">
                      <v-icon small color="primary" class="mr-1">mdi-reply</v-icon>
                      {{ button }}
                    </button>
                  </div>
                </div>
              </div>
            </template>

            <div v-if="sending" class="sandbox-row sandbox-row-chatbot">
              <div class="sandbox-message sandbox-message-chatbot sandbox-typing" aria-label="Digitando">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <div class="sandbox-composer px-4 py-3">
            <div v-if="ended" class="sandbox-ended d-flex align-center flex-wrap">
              <v-icon color="warning" class="mr-2">mdi-chat-remove-outline</v-icon>
              <div class="flex-grow-1 mr-2">
                <div class="sandbox-ended-title">Conversa encerrada</div>
                <div class="sandbox-ended-text">O bot finalizou ou transferiu a conversa. Reinicie para testar de novo.</div>
              </div>
              <ib-btn color="primary" class="my-1" @click="restart">
                <v-icon left>mdi-restart</v-icon>
                Reiniciar teste
              </ib-btn>
            </div>

            <template v-else>
              <div class="d-flex align-end">
                <v-textarea ref="input" v-model="text" outlined background-color="gray25" rows="1" auto-grow no-resize hide-details :disabled="limitReached" :placeholder="limitReached ? 'Limite de mensagens atingido. Reinicie o teste.' : 'Digite uma mensagem como se fosse o contato'" class="sandbox-input nd-text-field" @keydown.enter="handleEnter" />

                <v-btn class="ml-2 mb-1" fab dark small color="primary" :loading="sending" :disabled="!text.trim() || limitReached" aria-label="Enviar mensagem" @click="send()">
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>

              <div class="sandbox-composer-hint d-flex mt-1">
                <span>Enter para enviar · Shift + Enter para quebrar linha</span>
                <v-spacer />
                <span v-if="session?.count">{{ session.count }}/{{ maxMessages }} mensagens</span>
              </div>
            </template>
          </div>
        </div>

        <v-expand-x-transition>
          <div v-show="showContactPanel" class="sandbox-panel ib-scroll" :class="{ 'sandbox-panel-mobile': isMobile }">
            <div class="pa-4">
              <div class="d-flex align-center mb-4">
                <v-avatar size="44" color="gray100" class="mr-3">
                  <span class="sandbox-panel-initials">{{ contactInitials }}</span>
                </v-avatar>
                <div class="sandbox-panel-identity">
                  <div class="sandbox-panel-name text-truncate">{{ currentContact.name || 'Contato de teste' }}</div>
                  <div class="sandbox-panel-phone">{{ currentContact.phone || '5511999999999' }}</div>
                </div>
                <v-spacer />
                <v-btn v-if="isMobile" icon aria-label="Voltar para a conversa" @click="showContactPanel = false">
                  <v-icon>mdi-message-outline</v-icon>
                </v-btn>
              </div>

              <template v-if="!editingContact">
                <div class="sandbox-panel-section">
                  <div class="sandbox-panel-label">Tags</div>
                  <div v-if="currentContact.tags.length" class="d-flex flex-wrap">
                    <ib-chip v-for="tag in currentContact.tags" :key="tag" small :color="tagColor(tag)" class="mr-1 mb-1">
                      {{ tagName(tag) }}
                    </ib-chip>
                  </div>
                  <div v-else class="sandbox-panel-empty">Nenhuma tag</div>
                </div>

                <div class="sandbox-panel-section">
                  <div class="sandbox-panel-label">Variáveis</div>
                  <div v-if="currentVariables.length">
                    <div v-for="variable in currentVariables" :key="variable.key" class="sandbox-variable">
                      <div class="sandbox-variable-key">{{ variable.key }}</div>
                      <div class="sandbox-variable-value">{{ variable.value }}</div>
                    </div>
                  </div>
                  <div v-else class="sandbox-panel-empty">Nenhuma variável</div>
                </div>

                <div class="sandbox-panel-hint mb-3">
                  O contato é gerado automaticamente. Para testes específicos, defina nome, telefone, tags ou variáveis iniciais.
                </div>

                <ib-btn block outlined color="primary" @click="editingContact = true">
                  <v-icon left>mdi-pencil-outline</v-icon>
                  Personalizar contato
                </ib-btn>
              </template>

              <template v-else>
                <ib-text-field v-model="contactForm.name" label="Nome" placeholder="Contato de teste" hide-details="auto" class="mb-3" />

                <ib-text-field v-model="contactForm.phone" label="Telefone" placeholder="5511999999999" type="tel" hide-details="auto" class="mb-3" />

                <ib-autocomplete v-model="contactForm.tags" :items="tags" item-text="name" item-value="_id" label="Tags iniciais" no-data-text="Não há tags" multiple outlined hide-details="auto" class="mb-3">
                  <template #selection="{ item }">
                    <ib-chip small :color="item.color" class="mr-1 my-1">
                      {{ item.name }}
                    </ib-chip>
                  </template>
                </ib-autocomplete>

                <div class="sandbox-panel-label mb-2">Variáveis iniciais</div>
                <div v-for="(variable, index) in contactForm.variables" :key="`variable_${index}`" class="d-flex align-center mb-2">
                  <ib-text-field v-model="variable.key" label="Nome" hide-details="auto" class="mr-2" />
                  <ib-text-field v-model="variable.value" label="Valor" hide-details="auto" class="mr-1" />
                  <v-btn icon small aria-label="Remover variável" @click="contactForm.variables.splice(index, 1)">
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </div>

                <ib-btn small text color="primary" class="mb-4" @click="contactForm.variables.push({ key: '', value: '' })">
                  <v-icon left small>mdi-plus</v-icon>
                  Adicionar variável
                </ib-btn>

                <div class="sandbox-panel-hint mb-3">Ao aplicar, a conversa de teste é reiniciada com esses dados.</div>

                <div class="d-flex">
                  <ib-btn outlined color="gray200" class="flex-grow-1 mr-2" @click="editingContact = false">
                    <div class="gray1000--text">Cancelar</div>
                  </ib-btn>
                  <ib-btn color="primary" class="flex-grow-1" @click="applyContact">
                    Aplicar
                  </ib-btn>
                </div>
              </template>
            </div>
          </div>
        </v-expand-x-transition>
      </div>
    </v-card>
  </ib-dialog>
</template>

<script>
import Api from '@/lib/Api';

export default {
  name: 'ChatbotSandbox',

  props: {
    value: Boolean,
    type: {
      type: String,
      default: 'ai_chatbot'
    },
    targetId: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      text: '',
      sending: false,
      session: null,
      sessionId: 0,
      messages: [],
      tags: [],
      maxMessages: 100,
      suggestions: ['Olá', 'Quero saber mais', 'Falar com um atendente'],
      showContactPanel: false,
      editingContact: false,
      contactForm: {
        name: '',
        phone: '',
        tags: [],
        variables: []
      }
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    ended() {
      return !!this.session?.ended;
    },

    limitReached() {
      return (this.session?.count || 0) >= this.maxMessages;
    },

    currentMode() {
      return this.session?.chatbot_type || this.type;
    },

    currentModeIcon() {
      return this.currentMode === 'flow_chatbot' ? 'mdi-sitemap-outline' : 'mdi-robot-outline';
    },

    currentModeLabel() {
      if (this.ended) return 'Conversa encerrada';
      if (this.sending) return 'digitando...';
      return this.currentMode === 'flow_chatbot' ? 'Fluxo de conversa' : 'Chatbot de IA';
    },

    currentContact() {
      const contact = this.session?.contact || this.newSession().contact;
      return { name: contact.name, phone: contact.phone, tags: contact.tags || [], data: contact.data || {} };
    },

    currentVariables() {
      return Object.keys(this.currentContact.data).map(key => {
        const value = this.currentContact.data[key];
        return { key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) };
      });
    },

    contactInitials() {
      const name = (this.currentContact.name || 'Contato de teste').trim();
      return name.split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.restart();
          this.editingContact = false;
          if (!this.tags.length) this.getTags();
        }
      }
    }
  },

  methods: {
    closeDialog() {
      this.dialog = false;
    },

    async getTags() {
      const resp = await Api.getTagsByUser();
      if (!resp.error && resp.message) this.tags = resp.message;
    },

    tagName(id) {
      return this.tags.find(t => t._id === id)?.name || 'Tag';
    },

    tagColor(id) {
      return this.tags.find(t => t._id === id)?.color;
    },

    async confirmRestart() {
      if (this.messages.length) {
        const confirmed = await this.$store.dispatch('confirmDialog', {
          title: 'Reiniciar teste',
          message: 'A conversa de teste atual será apagada e o bot começará do início. Deseja continuar?'
        });

        if (!confirmed) return;
      }

      this.restart();
    },

    restart() {
      this.sessionId++;
      this.session = null;
      this.messages = [];
      this.text = '';
      this.sending = false;
    },

    applyContact() {
      this.editingContact = false;
      this.restart();
      if (this.isMobile) this.showContactPanel = false;
    },

    newSession() {
      const data = {};
      this.contactForm.variables.forEach(v => {
        if (v.key && v.key.trim()) data[v.key.trim()] = v.value;
      });

      return {
        chatbot_type: this.type,
        chatbot: this.type === 'ai_chatbot' ? this.targetId : undefined,
        flow: this.type === 'flow_chatbot' ? this.targetId : undefined,
        last_flow_context: '',
        contact: {
          name: this.contactForm.name.trim(),
          phone: this.contactForm.phone.trim(),
          tags: [...this.contactForm.tags],
          data
        }
      }
    },

    handleEnter(event) {
      if (event.shiftKey) return;
      event.preventDefault();
      this.send();
    },

    async send(value) {
      const text = (typeof value === 'string' ? value : this.text).trim();
      if (!text || this.sending || this.ended || this.limitReached) return;

      if (!this.session) this.session = this.newSession();

      const sessionId = this.sessionId;
      this.messages.push({ type: 'text', origin: 'customer', text, created: new Date() });
      this.text = '';
      this.sending = true;
      this.scrollToBottom();

      const resp = await Api.sandboxMessage({ session: this.session, text });

      //TESTE REINICIADO ENQUANTO A RESPOSTA CHEGAVA
      if (sessionId !== this.sessionId) return;

      this.sending = false;

      if (!resp.error && resp.message) {
        this.session = resp.message.session;
        (resp.message.messages || []).forEach(m => this.messages.push({ ...m, origin: 'chatbot', created: new Date() }));
      }

      this.scrollToBottom();
      this.$nextTick(() => this.$refs.input?.focus());
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chat = this.$refs.chat;
        if (chat) chat.scrollTo({ top: chat.scrollHeight, behavior: 'smooth' });
      });
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },

    formatText(text = '') {
      const escaped = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return this.$utils.renderBoldText(escaped);
    }
  }
}
</script>

<style scoped lang="scss">
.sandbox-card {
  height: 82vh;
  max-height: 820px;
  overflow: hidden;
  padding-bottom: 0 !important;
}

.v-dialog--fullscreen .sandbox-card {
  height: 100%;
  max-height: none;
}

.sandbox-header-info {
  min-width: 0;
}

.sandbox-header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--v-gray950-base);
  line-height: 1.3;
}

.sandbox-header-subtitle {
  font-size: 12px;
  color: var(--v-gray500-base);
}

.sandbox-body {
  min-height: 0;
  position: relative;
}

.sandbox-chat-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.sandbox-chat {
  overflow-y: auto;
  padding: 16px;
  background-color: var(--v-gray50-base);
  background-image: radial-gradient(var(--v-gray100-base) 1px, transparent 1px);
  background-size: 18px 18px;
}

.sandbox-empty {
  height: 100%;
  max-width: 380px;
  margin: 0 auto;
}

.sandbox-empty-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--v-gray950-base);
}

.sandbox-empty-text {
  font-size: 14px;
  line-height: 1.5;
  color: var(--v-gray500-base);
}

.sandbox-row {
  display: flex;
  margin-top: 6px;
  animation: sandbox-in 180ms ease-out;
}

.sandbox-row-customer {
  justify-content: flex-end;
}

.sandbox-row-chatbot {
  justify-content: flex-start;
}

.sandbox-row-event {
  justify-content: center;
  margin: 12px 0;
}

.sandbox-message-group {
  max-width: 75%;
  display: flex;
  flex-direction: column;
}

.sandbox-row-customer .sandbox-message-group {
  align-items: flex-end;
}

.sandbox-message {
  padding: 8px 10px 4px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-line;
  word-break: break-word;
  box-shadow: 0px 1px 2px 0px #0D0D120F;
}

.sandbox-message-customer {
  background-color: var(--v-green100-base);
  border: 1px solid var(--v-green200-base);
  color: var(--v-gray1000-base);
  border-bottom-right-radius: 4px;
}

.sandbox-message-chatbot {
  background-color: var(--v-gray25-base);
  border: 1px solid var(--v-gray100-base);
  color: var(--v-gray950-base);
  border-bottom-left-radius: 4px;
}

.sandbox-message-time {
  text-align: right;
  font-size: 11px;
  margin-top: 2px;
  color: var(--v-gray400-base);
}

.sandbox-media {
  max-width: 100%;
  border-radius: 8px;
}

.sandbox-audio {
  max-width: 260px;
}

.sandbox-document {
  padding: 8px;
  border-radius: 8px;
  background-color: var(--v-gray50-base);
  color: var(--v-gray950-base);
  text-decoration: none;
}

.sandbox-buttons {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  width: 100%;
  min-width: 220px;
}

.sandbox-button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-top: 4px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid var(--v-gray100-base);
  background-color: var(--v-gray25-base);
  color: var(--v-primary-base);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 150ms ease-out;

  &:hover:not(:disabled) {
    background-color: var(--v-green50-base);
  }

  &:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.sandbox-event {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 85%;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.5;
  background-color: rgba(250, 204, 21, 0.12);
  border: 1px solid rgba(234, 179, 8, 0.35);
  color: #7a5b00;

  .v-icon {
    color: inherit;
  }
}

.theme--dark .sandbox-event {
  background-color: rgba(250, 204, 21, 0.1);
  border-color: rgba(250, 204, 21, 0.28);
  color: #f5d77a;
}

.sandbox-typing {
  display: flex;
  align-items: center;
  padding: 12px 14px;

  span {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    border-radius: 50%;
    background-color: var(--v-gray400-base);
    animation: sandbox-typing 1.2s infinite ease-in-out;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

.sandbox-composer {
  border-top: 1px solid var(--v-gray100-base);
  background-color: var(--v-gray25-base);
}

.sandbox-input ::v-deep textarea {
  max-height: 140px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.sandbox-composer-hint {
  font-size: 11px;
  color: var(--v-gray400-base);
}

.sandbox-ended {
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid var(--v-gray100-base);
  background-color: var(--v-gray50-base);
}

.sandbox-ended-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--v-gray950-base);
}

.sandbox-ended-text {
  font-size: 12px;
  color: var(--v-gray500-base);
}

.sandbox-panel {
  width: 400px;
  flex-shrink: 0;
  overflow-y: auto;
  border-left: 1px solid var(--v-gray100-base);
  background-color: var(--v-gray25-base);
}

.sandbox-panel-mobile {
  width: 100%;
  border-left: none;
}

.sandbox-panel-initials {
  font-size: 15px;
  font-weight: 600;
  color: var(--v-gray600-base);
}

.sandbox-panel-identity {
  min-width: 0;
}

.sandbox-panel-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--v-gray950-base);
}

.sandbox-panel-phone {
  font-size: 13px;
  color: var(--v-gray500-base);
}

.sandbox-panel-section {
  margin-bottom: 20px;
}

.sandbox-panel-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--v-gray500-base);
  margin-bottom: 8px;
}

.sandbox-panel-empty,
.sandbox-panel-hint {
  font-size: 13px;
  line-height: 1.5;
  color: var(--v-gray400-base);
}

.sandbox-variable {
  padding: 6px 0;
  border-bottom: 1px solid var(--v-gray100-base);

  &:last-child {
    border-bottom: none;
  }
}

.sandbox-variable-key {
  font-size: 12px;
  color: var(--v-gray500-base);
}

.sandbox-variable-value {
  font-size: 13px;
  font-family: monospace;
  color: var(--v-gray950-base);
  word-break: break-all;
}

@keyframes sandbox-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes sandbox-typing {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sandbox-row,
  .sandbox-typing span {
    animation: none;
  }
}
</style>
